import { apiUrl } from "@/config/env";
import { tokenStore } from "@/features/auth/stores/token-store";
import { localizeApiErrorMessage } from "@/lib/error-messages";

export class ApiError extends Error {
  public readonly rawMessage?: string;

  constructor(
    public readonly status: number,
    public readonly code: string,
    message?: string,
    public readonly issues?: unknown[],
  ) {
    super(localizeApiErrorMessage(status, code, message));
    this.name = "ApiError";
    this.rawMessage = message;
  }
}

interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "DELETE";
  json?: unknown;
  form?: FormData;
  query?: Record<string, string | number | undefined | null>;
  /** refresh エンドポイント用。Cookie を送信する */
  withCredentials?: boolean;
  /** 内部用。401 リトライを防ぐためのフラグ */
  _retried?: boolean;
}

function buildUrl(path: string, query?: RequestOptions["query"]): string {
  const url = apiUrl(path);
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === "") continue;
    params.set(k, String(v));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

async function parseBody(res: Response): Promise<unknown> {
  const ct = res.headers.get("content-type") ?? "";
  if (ct.includes("application/json")) return res.json();
  return res.text();
}

async function attemptRefresh(): Promise<boolean> {
  try {
    const res = await fetch(apiUrl("/api/admin/auth/refresh"), {
      method: "POST",
      credentials: "include",
    });
    if (!res.ok) {
      tokenStore.set(null);
      return false;
    }
    const data = (await res.json()) as {
      ok: true;
      accessToken: string;
      expiresIn: number;
    };
    tokenStore.set(data.accessToken);
    return true;
  } catch {
    tokenStore.set(null);
    return false;
  }
}

let refreshInFlight: Promise<boolean> | null = null;
export async function singletonRefresh(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = attemptRefresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

export async function apiFetch<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const res = await apiFetchResponse(path, options);
  return (await parseBody(res)) as T;
}

export async function apiFetchResponse(
  path: string,
  options: RequestOptions = {},
): Promise<Response> {
  const headers: Record<string, string> = {};
  if (options.json !== undefined) headers["Content-Type"] = "application/json";
  const token = tokenStore.get();
  if (token && !options.withCredentials) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const res = await fetch(buildUrl(path, options.query), {
    method: options.method ?? "GET",
    headers,
    body:
      options.json !== undefined
        ? JSON.stringify(options.json)
        : options.form,
    credentials: options.withCredentials ? "include" : "omit",
  });

  if (res.status === 401 && !options._retried && !options.withCredentials) {
    const ok = await singletonRefresh();
    if (ok) {
      return apiFetchResponse(path, { ...options, _retried: true });
    }
  }

  if (!res.ok) {
    const body = (await parseBody(res)) as
      | { ok: false; code: string; message?: string; issues?: unknown[] }
      | string;
    const err = body as {
      ok: false;
      code: string;
      message?: string;
      issues?: unknown[];
    };
    throw new ApiError(
      res.status,
      err.code ?? "INTERNAL",
      err.message,
      err.issues,
    );
  }
  return res;
}

export const api = {
  get: <T>(path: string, query?: RequestOptions["query"]) =>
    apiFetch<T>(path, { method: "GET", query }),
  getResponse: (path: string, query?: RequestOptions["query"]) =>
    apiFetchResponse(path, { method: "GET", query }),
  getText: (path: string, query?: RequestOptions["query"]) =>
    apiFetch<string>(path, { method: "GET", query }),
  post: <T>(path: string, json?: unknown, opts?: Partial<RequestOptions>) =>
    apiFetch<T>(path, { method: "POST", json, ...opts }),
  postForm: <T>(path: string, form: FormData) =>
    apiFetch<T>(path, { method: "POST", form }),
  patch: <T>(path: string, json?: unknown) =>
    apiFetch<T>(path, { method: "PATCH", json }),
  delete: <T>(path: string) => apiFetch<T>(path, { method: "DELETE" }),
};
